import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomFileUploader from "../../components/common/CustomFileUploader";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button as MUIButton,
  Tooltip as MUITooltip,
  Avatar,
  Chip,
} from "@mui/material";
import {
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiExternalLink,
} from "react-icons/fi";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import { Formik } from "formik";
import * as Yup from "yup";

import "./Agents.css";
import apis from "../../utils/apis";
import httpAction from "../../utils/httpAction";
import toast from "react-hot-toast";

export default function Agents() {
  // Demo data (replace with API later)
  const [rows, setRows] = useState([]);
  const formikRef = useRef(null);
  const [q, setQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [urls, setUrls] = useState([]);
  const [branchDrop, setBranchDrop] = useState([]);

  const initialValues = {
    imageUrl: editing?.imageUrl ?? null,
    name: editing?.name || "",
    mobile: editing?.mobile || "",
    branch: editing?.branch || "",
    facebook: editing?.facebook || "",
    experience: editing?.experience || "",
    desigination: editing?.desigination || "",
  };

  const schema = Yup.object({
    name: Yup.string().trim().min(2, "Too short").required("Name is required"),
    desigination: Yup.string()
      .trim()
      .min(2, "Too short")
      .required("Desigination is required"),
    mobile: Yup.string()
      .trim()
      .matches(/^[0-9+ -]{7,20}$/, "Enter a valid mobile number")
      .required("Mobile is required"),
    experience: Yup.number()
      .typeError("Experience must be a number")
      .integer("Experience must be a whole number")
      .min(0, "Experience cannot be negative")
      .max(60, "Experience looks unrealistic")
      .required("Experience is required"),

    branch: Yup.string().trim().required("Branch is required"),
    facebook: Yup.string()
      .trim()
      .url("Enter a valid URL")
      .nullable()
      .notRequired(),

    imageUrl: Yup.string().required("Profile picture is required"),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((r) => {
      return (
        String(r.id).includes(needle) ||
        r.name.toLowerCase().includes(needle) ||
        r.mobile.toLowerCase().includes(needle) ||
        r.branch.toLowerCase().includes(needle)
      );
    });
  }, [rows, q]);

  const openAdd = () => {
    setEditing(null);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setUrls([{ url: row?.imageUrl }]);
    setEditing(row);
    setShowModal(true);
  };

  useEffect(() => {
    formikRef?.current?.setFieldValue("imageUrl", editing?.imageUrl);
  }, [editing]);

  const removeRow = async (id) => {
    const data = {
      url: apis().deleteUser + "?id=" + id,
      method: "DELETE",
    };

    const result = await httpAction(data);
    if (result?.status) {
      toast.success(result?.message);
      setRows((prev) => prev.filter((r) => r._id !== id));
    }
  };

  const saveUser = async (values) => {
    const data = {
      url: editing?.name
        ? apis().updateUser + "?id=" + editing?._id
        : apis().addUser,
      method: editing?.name ? "PUT" : "POST",
      body: values,
    };

    const result = await httpAction(data);

    if (result?.status) {
      toast.success(result?.message);
      setShowModal(false);

      if (editing?.name) {
        const findedIndex = rows.findIndex(
          (item) => item?._id === editing?._id
        );

        const userCopy = [...rows];
        userCopy.splice(findedIndex, 1, result?.user);
        setRows(userCopy);
      } else {
        setRows((prev) => [...prev, result?.user]);
      }
      setEditing(null);
      setUrls([]);
    }
  };

  const getUsers = async () => {
    const data = {
      url: apis().getUserList,
    };

    const result = await httpAction(data);
    if (result?.status) {
      setRows(result?.list);
     
    }
  };

  const branchList = async () => {
    const data = {
      url: apis().branchList,
    };

    const result = await httpAction(data);
    if (result?.status) {
      setBranchDrop(result?.list);
    }
  };

  useEffect(() => {
    getUsers();
    branchList();
  }, []);

  return (
    <Box className="usersPage">
      <Box className="usersHeader">
        <Box>
          <Typography className="usersTitle">Users</Typography>
          <Typography className="usersSub">
            Create and manage users/agents with branch assignment and listing
            counts.
          </Typography>
        </Box>

        <MUIButton
          variant="contained"
          className="usersAddBtn"
          startIcon={<FiPlus />}
          onClick={openAdd}
        >
          Add New User
        </MUIButton>
      </Box>

      <Paper className="usersCard" elevation={0}>
        <Box className="usersToolbar">
          <TextField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, mobile, branch, id..."
            size="small"
            className="usersSearch"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch />
                </InputAdornment>
              ),
            }}
          />
          <Chip
            label={`${filtered.length} result(s)`}
            size="small"
            className="usersCount"
          />
        </Box>

        <Box className="usersTableWrap">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="th">ID</TableCell>
                <TableCell className="th">User</TableCell>
                <TableCell className="th">Mobile</TableCell>
                <TableCell className="th">Branch</TableCell>
                <TableCell className="th">Listings</TableCell>
                <TableCell className="th">Facebook</TableCell>
                <TableCell className="th" align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="emptyRow">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r, index) => (
                  <TableRow key={r.id} hover className="tr">
                    <TableCell className="idCell">{index + 1}</TableCell>

                    <TableCell>
                      <Box className="userCell">
                        <Avatar
                          src={`${apis().localUrl + r?.imageUrl}` || undefined}
                          alt={r?.name}
                          className="userAvatar"
                        >
                          {r?.name?.[0] || "U"}
                        </Avatar>
                        <Box className="userMeta">
                          <Typography className="userName">{r?.name}</Typography>
                          <Typography className="userMini">
                            {r?.imageUrl ? "Has photo" : "No photo"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell className="mono">{r?.mobile}</TableCell>
                    <TableCell className="branchCell">{r?.branch}</TableCell>

                    <TableCell>
                      <span className="listingsPill">{r?.listing?.length}</span>
                    </TableCell>

                    <TableCell>
                      <a
                        className="fbLink"
                        href={r.facebook || apis().facebookLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View <FiExternalLink />
                      </a>
                    </TableCell>

                    <TableCell align="right">
                      <Box className="actions">
                        <MUITooltip title="Edit">
                          <IconButton
                            size="small"
                            className="iconBtn"
                            onClick={() => openEdit(r)}
                          >
                            <FiEdit2 />
                          </IconButton>
                        </MUITooltip>

                        <MUITooltip title="Delete">
                          <IconButton
                            size="small"
                            className="iconBtn danger"
                            onClick={() => removeRow(r._id)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        </MUITooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditing(null);
          setUrls([]);
        }}
        centered
      >
        <Formik
          enableReinitialize
          key={editing?._id || "new"}
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={(values, helpers) => {
            saveUser(values);
            helpers.setSubmitting(false);
          }}
          innerRef={formikRef}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {editing ? "Edit User" : "Add New User"}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <div className="modalGrid">
                  <div className="modalField modalField--full">
                    <CustomFileUploader
                      multiple={false}
                      onEdit={urls}
                      onChange={(file) => {
                        const url = file[0];
                        formik.setFieldValue("imageUrl", url?.url);
                      }}
                    />

                    {formik.touched.imageUrl && formik.errors.imageUrl ? (
                      <div className="invalid-feedback d-block">
                        {formik.errors.imageUrl}
                      </div>
                    ) : null}
                  </div>

                  <div className="modalField">
                    <label className="modalLabel">Name</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formik.touched.name && formik.errors.name
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="User name"
                      {...formik.getFieldProps("name")}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <div className="invalid-feedback">
                        {formik.errors.name}
                      </div>
                    ) : null}
                  </div>

                  <div className="modalField">
                    <label className="modalLabel">Mobile</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formik.touched.mobile && formik.errors.mobile
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="+92 3xx xxxxxxx"
                      {...formik.getFieldProps("mobile")}
                    />
                    {formik.touched.mobile && formik.errors.mobile ? (
                      <div className="invalid-feedback">
                        {formik.errors.mobile}
                      </div>
                    ) : null}
                  </div>

                  <div className="modalField">
                    <label className="modalLabel">Branch Name</label>

                    <select
                      className={`form-select ${
                        formik.touched.branch && formik.errors.branch
                          ? "is-invalid"
                          : ""
                      }`}
                      {...formik.getFieldProps("branch")}
                    >
                      <option value="" disabled>
                        Select branch...
                      </option>

                      {branchDrop?.map((b) => (
                        <option key={b} value={b?.name}>
                          {b?.name}
                        </option>
                      ))}
                    </select>

                    {formik.touched.branch && formik.errors.branch ? (
                      <div className="invalid-feedback">
                        {formik.errors.branch}
                      </div>
                    ) : null}
                  </div>

                  <div className="modalField">
                    <label className="modalLabel">Enter Your Experience</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formik.touched.experience && formik.errors.experience
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="6 years"
                      {...formik.getFieldProps("experience")}
                    />
                    {formik.touched.experience && formik.errors.experience ? (
                      <div className="invalid-feedback">
                        {formik.errors.experience}
                      </div>
                    ) : null}
                  </div>

                  <div className="modalField">
                    <label className="modalLabel">
                      Enter Your Desigination
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        formik.touched.desigination &&
                        formik.errors.desigination
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Manager etc"
                      {...formik.getFieldProps("desigination")}
                    />
                    {formik.touched.desigination &&
                    formik.errors.desigination ? (
                      <div className="invalid-feedback">
                        {formik.errors.desigination}
                      </div>
                    ) : null}
                  </div>

                  <div className="modalField">
                    <label className="modalLabel">Facebook Link</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formik.touched.facebook && formik.errors.facebook
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="https://facebook.com/username"
                      {...formik.getFieldProps("facebook")}
                    />
                    {formik.touched.facebook && formik.errors.facebook ? (
                      <div className="invalid-feedback">
                        {formik.errors.facebook}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditing(null);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  Save
                </Button>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>
    </Box>
  );
}
